import cx from 'classnames'
import Styles from './BatchModals.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Pressable } from '@react-aria/interactions'
import { Image } from './Image'
import { ImageAdd, Upload5 } from 'blaze-ui/icons'
import { MinimalMediaFile } from '../../Types'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { useCreateMediaFile } from 'hooks/queries/mediaFiles/useCreateMediaFile'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useModals } from 'almanac-ui/Modal'
import {
  ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
  ACCEPTED_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { Background } from 'blaze-ui/icons/32'
import { Button } from 'blaze-ui/Button'

interface Props {
  mediaFiles: MinimalMediaFile[]
  setMediaFiles: React.Dispatch<React.SetStateAction<MinimalMediaFile[]>>
  setIsProcessing: (value: boolean) => void
}

export const Images = ({ mediaFiles, setMediaFiles, setIsProcessing }: Props) => {
  const { closeModal, openModal } = useModals()
  const { mutateAsync: createMediaFile } = useCreateMediaFile()
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProject = useUploadsProject(workspaceId)?.data?.project

  const openImageMenuModal = () => {
    openModal(
      ImageMenuModal,
      {
        onPressOutside: closeModal,
        accept: ACCEPTED_IMAGE_MIME_TYPES_NO_SVG + ',' + ACCEPTED_VIDEO_MIME_TYPES,
        hiddenProviders: ['gifs'],
        uploadMedia: true,
        onUse: async (images) => {
          if (!uploadsProject) {
            showToastMessage('Unable to add media. Try again. Contact support if the error persists.', 'error')
            return
          }
          const dismiss = showToastMessage('Processing...', 'loading', undefined, 0)
          setIsProcessing(true)
          let newFiles: { id: string; thumbnail_url?: string; source_url?: string; duration?: number }[] = []

          await Promise.all(
            images.map(async (file) => {
              const mediaFile = await createMediaFile({
                source_url: file.source_url || file.url,
                mime_type: file.mime_type || 'image/jpeg',
                title: file.original_filename || file.url,
                project_key: uploadsProject.id,
                show_in_file_system: false,
              })
              const { id, thumbnail_url, source_url } = mediaFile
              newFiles.push({ id, source_url, thumbnail_url, duration: file.duration })
            }),
          )

          setMediaFiles((prev) => [...prev, ...newFiles])
          dismiss()
          setIsProcessing(false)
        },
        maxCount: Infinity,
      },
      {
        newStack: true,
      },
    )
  }
  return (
    <div className={Styles.images}>
      <div className={Styles.label}>
        <Heading level={5}>Use specific images and videos</Heading>
        <Text color="var(--dark-60)" variant="placeholder">
          Optional
        </Text>
      </div>
      <div className={Styles.imagesContainer}>
        {mediaFiles.map((mediaFile) => (
          <Image
            key={mediaFile.id}
            src={mediaFile.thumbnail_url || mediaFile.source_url}
            duration={mediaFile.duration}
            onRemove={() => setMediaFiles((prev) => prev.filter((file) => file.id !== mediaFile.id))}
          />
        ))}
        {mediaFiles.length > 0 && (
          <Pressable onPress={openImageMenuModal}>
            <div className={cx(Styles.addMediaButton)}>
              <ImageAdd size={20} color="var(--selected-fill)" />
            </div>
          </Pressable>
        )}
        {mediaFiles.length === 0 && (
          <div className={Styles.emptyStateContainer}>
            <div className={Styles.emptyState}>
              <Background size={58} />
              <Button variant="secondary" frontIcon={Upload5} onPress={openImageMenuModal}>
                Choose Files
              </Button>
              <Text variant="placeholder" color="var(--dark-60)">
                Blaze supports png, jpg, jpeg, mp4, mpeg, avi
              </Text>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
